import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import apiClient from '@/services/api-client';
import { ApiResponse } from '@/services/types';
import { API_ENDPOINTS } from '@/utils/constants/end-points';

/** A row of dbo.DateFormats, as returned by GET clientOnboarding/date-formats. */
export interface DateFormatItem {
  id: number;
  code: string;
  /** 1 = Date, 2 = Time. */
  type: number;
  displayLabel: string;
  /** .NET custom pattern, e.g. dd/MM/yyyy or hh:mm tt. */
  pattern: string;
  isDefault: boolean;
}

/** A dropdown option: the format label as is, e.g. "DD-MMM-YYYY". */
export interface DateFormatOption extends DateFormatItem {
  name: string;
}

export const DATE_FORMAT_TYPE = { date: 1, time: 2 } as const;

const toOption = (item: DateFormatItem): DateFormatOption => ({
  ...item,
  name: item.displayLabel,
});

interface DateFormatState {
  data: DateFormatOption[];
  loading: boolean;
  error: string | null;
}

// ---------------------------
//  ASYNC THUNKS
// ---------------------------

// GET LIST (both types; the screen splits them by `type`)
export const fetchDateFormats = createAsyncThunk<DateFormatOption[]>(
  'dateFormats/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiClient.get<ApiResponse<DateFormatItem[]>>(
        `${API_ENDPOINTS.ONBOARDING}/date-formats`
      );
      return (res.data.data ?? []).map(toOption);
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ---------------------------
//  SLICE
// ---------------------------

const initialState: DateFormatState = {
  data: [],
  loading: false,
  error: null,
};

const dateFormatSlice = createSlice({
  name: 'dateFormats',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchDateFormats.pending, state => {
      state.loading = true;
    });
    builder.addCase(fetchDateFormats.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload;
    });
    builder.addCase(fetchDateFormats.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export default dateFormatSlice.reducer;
